import { FC, useState } from 'react';
import { Box, CircularProgress, Skeleton } from '@mui/material';
import { SerializedStyles } from '@emotion/react';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';

// The HoS logo is a bitmap wrapped in an SVG, so load it by URL rather than inlining 11KB into every image
import hosLogoUrl from 'src/assets/svg/hos-logo-black.svg';
import { ReactComponent as VitaLogo } from 'src/assets/svg/vita-logo.svg';
import { getTheme } from 'src/utils/getTheme';

import { styles } from './styles';

const { isHosTheme } = getTheme();

type LoadStatus = 'loading' | 'loaded' | 'error';

interface LoadingImageProps {
    src: string;
    alt: string;
    imageStyles?: SerializedStyles;
    // High priority images download straight away, low priority ones lazy load behind them
    priority?: 'high' | 'low';
    // A small, fast image (e.g. a thumbnail) shown blurred while the full image downloads
    placeholderSrc?: string;
}

// Shows a skeleton until the image has downloaded, then fades the image in.
// Render with key={src} so the loading state resets when the image changes.
export const LoadingImage: FC<LoadingImageProps> = ({
    src,
    alt,
    imageStyles,
    priority = 'high',
    placeholderSrc,
}) => {
    const [status, setStatus] = useState<LoadStatus>('loading');

    // Cached images can finish loading before React attaches onLoad, so check on mount too
    const handleRef = (node: HTMLImageElement | null) => {
        if (node?.complete && status === 'loading') {
            setStatus(node.naturalWidth > 0 ? 'loaded' : 'error');
        }
    };

    return (
        <Box css={styles.loadingImage} aria-busy={status === 'loading'}>
            {status === 'loading' && (
                <Skeleton variant="rectangular" css={styles.loadingImageSkeleton} />
            )}

            {/* Kept behind the full image once loaded so its fade in doesn't flash the background */}
            {placeholderSrc && status !== 'error' && (
                <img
                    src={placeholderSrc}
                    alt=""
                    aria-hidden
                    css={styles.loadingImagePlaceholder}
                    // A broken placeholder would show the browser's broken image icon over the skeleton
                    onError={(event) => {
                        event.currentTarget.style.display = 'none';
                    }}
                />
            )}

            {/* Shows the blurred placeholder is a loading state rather than the actual photo */}
            {placeholderSrc && status === 'loading' && (
                <Box css={styles.loadingImageLogo} aria-hidden>
                    <CircularProgress size={72} thickness={2} css={styles.loadingImageSpinner} />
                    {isHosTheme ? (
                        <img src={hosLogoUrl} alt="" css={styles.loadingImageLogoIcon} />
                    ) : (
                        <VitaLogo css={styles.loadingImageLogoIcon} />
                    )}
                </Box>
            )}

            {status === 'error' ? (
                <Box css={styles.loadingImageError} role="img" aria-label={alt}>
                    <BrokenImageOutlinedIcon />
                </Box>
            ) : (
                <img
                    ref={handleRef}
                    src={src}
                    alt={alt}
                    loading={priority === 'low' ? 'lazy' : 'eager'}
                    decoding="async"
                    // React 18.2 doesn't recognise fetchPriority, so pass the lowercase attribute
                    {...{ fetchpriority: priority }}
                    css={[
                        imageStyles,
                        styles.loadingImageImg,
                        status === 'loaded' && styles.loaded,
                    ]}
                    onLoad={() => setStatus('loaded')}
                    onError={() => setStatus('error')}
                />
            )}
        </Box>
    );
};
